// Helpers puros para el listado paginado en servidor (004/005).
// Sin dependencias de React: se prueban con vitest.

export const SERVER_ORDER_FIELDS = [
  "date",
  "id",
  "identificationNumber",
  "type",
  "risk",
  "language",
  "source",
];

export const DEFAULT_PAGE_SIZE = 10;

export const mapSortingToOrder = (sorting) => {
  if (!Array.isArray(sorting) || sorting.length === 0) {
    return null;
  }
  const first = sorting[0] || {};
  if (!SERVER_ORDER_FIELDS.includes(first.id)) {
    return null;
  }
  return {
    orderField: first.id,
    orderDirection: first.desc ? "DESC" : "ASC",
  };
};

export const extractServerFilters = (columnFilters) => {
  let search = "";
  let risk = null;
  if (Array.isArray(columnFilters)) {
    for (const filter of columnFilters) {
      if (filter?.id === "identificationNumber") {
        search = filter?.value ?? "";
      }
      if (filter?.id === "risk") {
        const value = filter?.value;
        risk = Array.isArray(value) ? value[0] ?? null : value ?? null;
      }
    }
  }
  return { search: search || "", risk: risk || null };
};

export const buildPageParams = ({
  type,
  pageIndex = 0,
  pageSize = DEFAULT_PAGE_SIZE,
  search = "",
  stateId = null,
  risk = null,
  sorting = [],
} = {}) => {
  const order = mapSortingToOrder(sorting);
  return {
    type,
    page: pageIndex + 1,
    pageSize,
    search: search || "",
    stateId: stateId ?? null,
    risk: risk ?? null,
    orderField: order?.orderField ?? null,
    orderDirection: order?.orderDirection ?? null,
  };
};

// Recorre el universo visible en lotes acotados (paginación de servidor),
// liberando cada lote tras procesarlo. Nunca reconstruye el array completo.
export const fetchCommunicationBatches = async (
  fetchPage,
  { type, search = "", batchSize = 100, onBatch } = {},
) => {
  if (typeof fetchPage !== "function") {
    throw new Error("fetchCommunicationBatches requires a fetchPage function");
  }
  if (typeof onBatch !== "function") {
    throw new Error("fetchCommunicationBatches requires an onBatch function");
  }
  const safeBatchSize = Math.min(Math.max(Number(batchSize) || 100, 1), 100);
  let page = 1;
  let totalPages = 1;
  let pagesRead = 0;
  do {
    const response = await fetchPage({
      type,
      page,
      pageSize: safeBatchSize,
      search,
    });
    const payload = response?.data || {};
    const rows = Array.isArray(payload.data) ? payload.data : [];
    const pagination = payload.pagination || {};
    totalPages = Number(pagination.totalPages ?? 1) || 1;
    const total = Number(pagination.total ?? rows.length) || 0;
    await onBatch(rows, { page, pageSize: safeBatchSize, total, totalPages });
    pagesRead += 1;
    if (rows.length < safeBatchSize || page >= totalPages || pagesRead > 10000) {
      break;
    }
    page += 1;
  } while (true);
};
// Tras borrar elementos de la página actual, calcula el total y la página
// resultantes: retrocede a la última página válida si la actual queda vacía.
export const computePageAfterDelete = ({
  total,
  pageSize,
  pageIndex,
  deletedCount = 1,
}) => {
  const newTotal = Math.max(0, (total || 0) - (deletedCount || 0));
  const lastPageIndex = Math.max(
    0,
    Math.ceil(newTotal / Math.max(1, pageSize)) - 1,
  );
  return {
    total: newTotal,
    pageIndex: Math.min(pageIndex, lastPageIndex),
  };
};

// Compatibilidad con servidores que aún devuelven el listado completo como array.
// La caché pertenece a la pantalla: nunca se comparte entre sesiones o tenants.
export const createCommunicationPageLoader = (requestPage) => {
  let legacyScope = null;
  let legacyRows = null;
  const pending = new Map();
  let generation = 0;
  let isLegacy = false;

  const load = async (params) => {
    const scope = JSON.stringify([params.type, params.search || "", params.risk || null]);
    const requestParams = isLegacy
      ? { ...params, stateId: null, orderField: null, orderDirection: null }
      : params;
    const requestKey = JSON.stringify(requestParams);
    const currentGeneration = generation;
    let response;
    if (legacyRows !== null && legacyScope === scope) {
      response = { data: legacyRows };
    } else {
      if (!pending.has(requestKey)) {
        pending.set(requestKey, requestPage(requestParams));
      }
      try {
        response = await pending.get(requestKey);
      } finally {
        pending.delete(requestKey);
      }
    }
    if (!Array.isArray(response?.data)) {
      if (!Array.isArray(response?.data?.data) || !response.data.pagination) {
        throw new Error("Formato de respuesta de comunicaciones no válido");
      }
      return response;
    }
    isLegacy = true;

    // El servidor legacy aplica búsqueda/riesgo, pero ignora page/pageSize.
    // Solo conservar el universo sin filtro de estado para calcular todos los KPIs.
    const universe = response.data;
    if (requestParams.stateId == null && currentGeneration === generation) {
      legacyScope = scope;
      legacyRows = universe;
    }
    const countsByState = {};
    for (const row of universe) {
      const stateId = row.state?.id;
      if (stateId != null) countsByState[stateId] = (countsByState[stateId] || 0) + 1;
    }
    let rows = params.stateId == null
      ? universe
      : universe.filter((row) => String(row.state?.id) === String(params.stateId));
    if (params.orderField) {
      const direction = params.orderDirection === "ASC" ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const left = a[params.orderField];
        const right = b[params.orderField];
        return (left < right ? -1 : left > right ? 1 : 0) * direction;
      });
    }
    const total = rows.length;
    const pageSize = Math.max(1, Number(params.pageSize) || 25);
    const page = Math.max(1, Number(params.page) || 1);
    // Las definiciones legacy viajan dentro de answers.question. Mantener todas,
    // incluidas las que no están contestadas en la página que se va a mostrar.
    const questions = new Map();
    for (const row of universe) {
      for (const answer of row.answers || []) {
        if (answer.question?.id != null) questions.set(answer.question.id, answer.question);
      }
    }
    return {
      ...response,
      data: {
        data: rows.slice((page - 1) * pageSize, page * pageSize),
        pagination: { page, pageSize, total, totalPages: Math.ceil(total / pageSize) },
        questions: [...questions.values()],
        countsByState,
      },
    };
  };
  return {
    load,
    clear: () => {
      generation += 1;
      legacyScope = null;
      legacyRows = null;
      pending.clear();
    },
  };
};
